import React, { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * Compose Email Modal for Admin
 * Sends emails from info@newaudia.com via SMTP
 */
const ComposeEmailModal = ({ 
  isOpen, 
  onClose, 
  recipientEmail, 
  recipientName 
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!body.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setIsSending(true);
    try {
      const response = await api.post('/admin/email/send', {
        to_email: recipientEmail,
        subject: subject.trim(),
        body: body.trim(),
        recipient_name: recipientName || null
      });

      if (response.data.success) {
        toast.success(`Email sent to ${recipientEmail}`);
        handleClose();
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setSubject('');
    setBody('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
            <Send className="w-5 h-5 text-cyan-400" />
            Compose Email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* From field (read-only) */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              From
            </label>
            <Input
              value="info@newaudia.com"
              disabled
              className="bg-slate-800/50 border-slate-600 text-slate-400 cursor-not-allowed"
            />
          </div>

          {/* To field (read-only) */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              To
            </label>
            <Input
              value={recipientEmail}
              disabled
              className="bg-slate-800/50 border-slate-600 text-slate-300 cursor-not-allowed"
            />
            {recipientName && (
              <p className="text-xs text-slate-500 mt-1">{recipientName}</p>
            )}
          </div>

          {/* Subject field */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Subject
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-cyan-500"
              data-testid="email-subject-input"
            />
          </div>

          {/* Body field */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Message
            </label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message here..."
              rows={8}
              className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-cyan-500 resize-none"
              data-testid="email-body-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Your message will be formatted with the NewAudia email template.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSending}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || !subject.trim() || !body.trim()}
            className="bg-cyan-600 hover:bg-cyan-500 text-white"
            data-testid="send-email-button"
          >
            {isSending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ComposeEmailModal;
